import React from 'react';
import { 
    CrisisManagementPlanResult, 
    BusinessResiliencyPlanResult, 
    EmergencyPreparednessPlanResult,
    LocalIncidentManagementResult
} from '@types';
import { md, Section, ICONS } from './ReportShared';
import { useReportActions } from './ReportActionsContext';

const AppliedPolicyTemplate: React.FC<{ result: any }> = ({ result }) => result.appliedTemplateId ? (
    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <span className="font-bold">Organization policies applied:</span> {result.appliedTemplateId}
    </div>
) : null;

const safeArray = <T,>(value: T[] | T | null | undefined): T[] => Array.isArray(value)
    ? value
    : value && typeof value === 'object' ? [value] : [];

const checklistLabel = (value: unknown): string => {
    if (typeof value === 'string')
        return value.trim();
    if (!value || typeof value !== 'object')
        return '';
    const item = value as Record<string, unknown>;
    return String(item.checklistName || item.name || item.title || item.label || '').trim();
};

// --- Crisis Management Display ---
export const CrisisManagementDisplay: React.FC<{ result: CrisisManagementPlanResult; onLaunchChecklist?: (checklistName: string) => void }> = ({ result, onLaunchChecklist }) => (
    <div className="space-y-6">
        <AppliedPolicyTemplate result={result} />
        <div className="bg-red-50 p-6 rounded-xl border border-red-200 shadow-sm relative overflow-hidden">
            <div className="absolute -top-4 -right-4 text-red-100 opacity-50">
                {ICONS.activity}
            </div>
            <h3 className="font-bold text-red-900 text-sm uppercase tracking-wider">Executive Overview (FEMA Compliant)</h3>
            <h2 className="text-3xl font-black text-red-800 mt-2 mb-3">{result.incidentType}</h2>
            <div className="inline-block px-3 py-1 bg-red-600 text-white font-bold rounded shadow-sm text-sm uppercase tracking-wider mb-4">
                Severity: {result.severityLevel}
            </div>
            <div className="bg-white/80 p-4 rounded text-red-900 text-sm border border-red-100 italic relative z-10 leading-relaxed">
                "{result.summary || result.executiveSummary}"
            </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 text-white mb-6">
            <h3 className="font-bold text-slate-300 text-sm uppercase flex items-center gap-2 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Template Creation & Implementation Guide
            </h3>
            <p className="text-sm text-slate-300 mb-2">
                This <strong>Crisis Management Plan</strong> is a deterministic algorithmic output. It is assembled using predefined architectural parameters configured within the <strong>Guided Template Builder Hub</strong>.
            </p>
            <ol className="list-decimal pl-5 text-sm text-slate-400 space-y-1">
                <li><strong>Configuration:</strong> Administrators define specific ICS role responsibilities, tactical response phases, and communication procedures within the Template Builder interface.</li>
                <li><strong>Compilation:</strong> These inputs are compiled into a formalized, machine-readable JSON schema mapped to Industry Standard Competencies.</li>
                <li><strong>Implementation:</strong> During an active crisis (e.g., {result.incidentType}), the AI synthesis engine retrieves this deterministic schema, injecting live intelligence and specific operational variables to generate the structured tactical playbook presented below.</li>
            </ol>
        </div>

        <Section title="Incident Command System (ICS)">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { title: "Incident Commander", icon: ICONS.shield, data: result.incidentCommandSystem?.incidentCommander, bg: "bg-red-50", border: "border-red-200", text: "text-red-800" },
                    { title: "Operations Section Chief", icon: ICONS.crosshair, data: result.incidentCommandSystem?.operationsChief, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800" },
                    { title: "Safety Officer", icon: ICONS.health, data: result.incidentCommandSystem?.safetyOfficer, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
                    { title: "Public Info Officer", icon: ICONS.globe, data: result.incidentCommandSystem?.publicInformationOfficer, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" }
                ].map((roleInfo, idx) => (
                    roleInfo.data ? (
                        <div key={idx} className={`${roleInfo.bg} border ${roleInfo.border} rounded-lg p-4 shadow-sm h-full flex flex-col`}>
                            <div className={`flex items-center gap-2 font-bold ${roleInfo.text} mb-2 uppercase text-xs tracking-wider border-b pb-2`}>
                                <span className="w-4 h-4">{roleInfo.icon}</span> {roleInfo.title}
                            </div>
                            <div className="font-bold text-slate-800 text-sm mb-3">Role: {roleInfo.data.role}</div>
                            <ul className="list-disc pl-4 space-y-1 text-xs text-slate-700 flex-grow">
                                {safeArray(roleInfo.data.responsibilities).map((task, j) => <li key={j}>{task}</li>)}
                            </ul>
                        </div>
                    ) : null
                ))}
            </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Section title="Tactical Response Protocols">
                <div className="space-y-4">
                    {safeArray(result.tacticalResponseProtocols).map((proto, i) => (
                        <div key={i} className="bg-white p-4 rounded-lg border shadow-sm border-l-4 border-l-slate-800">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="font-bold text-slate-800 text-sm">{proto.phase}</h4>
                                <span className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-xs font-bold border">{proto.timeframe}</span>
                            </div>
                            <ul className="space-y-2">
                                {safeArray(proto.immediateActions).map((action, j) => (
                                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                                        <div className="mt-1 flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">{j + 1}</div>
                                        <span className="pt-0.5">{action}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title="Evacuation & Life Safety">
                <div className="bg-white rounded-lg border shadow-sm p-5 space-y-6">
                    <div>
                        <h4 className="flex items-center text-sm font-bold text-green-700 uppercase tracking-wider mb-2 border-b pb-1">
                            {ICONS.map} Muster Points
                        </h4>
                        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                            {safeArray(result.evacuationAndLifeSafety?.musterPoints).map((mp, i) => <li key={i}>{mp}</li>)}
                        </ul>
                    </div>
                    <div>
                        <h4 className="flex items-center text-sm font-bold text-red-700 uppercase tracking-wider mb-2 border-b pb-1">
                            {ICONS.key} Lockdown Procedures
                        </h4>
                        <ul className="space-y-2">
                            {safeArray(result.evacuationAndLifeSafety?.lockdownProcedures).map((lp, i) => (
                                <li key={i} className="flex gap-2 text-sm text-slate-700 items-start">
                                    <span className="text-red-500 font-bold">↳</span> <span>{lp}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h4 className="flex items-center text-sm font-bold text-blue-700 uppercase tracking-wider mb-2 border-b pb-1">
                            {ICONS.health} Medical Response
                        </h4>
                        <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                            {safeArray(result.evacuationAndLifeSafety?.medicalResponse).map((mr, i) => <li key={i}>{mr}</li>)}
                        </ul>
                    </div>
                </div>
            </Section>
        </div>

        {result.businessContinuityTransition && (
            <Section title="Business Continuity Transition">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-indigo-50 border border-indigo-200 rounded p-4">
                        <h4 className="font-bold text-indigo-900 text-sm mb-2">{ICONS.alert} Critical Functions At Risk</h4>
                        <ul className="list-disc pl-5 text-sm text-indigo-800 space-y-1">
                            {safeArray(result.businessContinuityTransition.criticalFunctionsAtRisk).map((func, i) => <li key={i}>{func}</li>)}
                        </ul>
                    </div>
                    <div className="bg-teal-50 border border-teal-200 rounded p-4">
                        <h4 className="font-bold text-teal-900 text-sm mb-2">{ICONS.clock} Recovery Objectives</h4>
                        <ul className="list-disc pl-5 text-sm text-teal-800 space-y-1">
                            {safeArray(result.businessContinuityTransition.recoveryObjectives).map((obj, i) => <li key={i}>{obj}</li>)}
                        </ul>
                    </div>
                </div>
            </Section>
        )}

        <Section title="Crisis Communication Protocol">
            <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                    <thead className="bg-slate-50 text-slate-600 uppercase text-xs font-bold border-b">
                        <tr>
                            <th className="px-5 py-4 w-1/4">Stakeholder</th>
                            <th className="px-5 py-4 w-1/2">Approved Message Template</th>
                            <th className="px-5 py-4 w-1/4">Primary Method</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {safeArray(result.crisisCommunicationMatrix).map((comm, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                                <td className="px-5 py-3 font-semibold text-slate-800 align-top">{comm.stakeholder}</td>
                                <td className="px-5 py-3 text-slate-600 italic bg-slate-50/50 align-top">"{comm.messageTemplate}"</td>
                                <td className="px-5 py-3 text-slate-600 align-top">
                                    <span className="px-2 py-1 bg-slate-100 border rounded text-xs font-bold text-slate-700 shadow-sm">{comm.method}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Section>

        {result.demobilizationAndRecovery && (
            <Section title="Demobilization & Post-Incident Review">
                <div className="bg-white p-5 rounded-lg border shadow-sm flex flex-col md:flex-row gap-6 items-center">
                    <div className="flex-grow">
                        <ul className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                            {safeArray(result.demobilizationAndRecovery.debriefingSteps).map((step, i) => <li key={i}>{step}</li>)}
                        </ul>
                    </div>
                    {result.demobilizationAndRecovery.afterActionReportRequired && (
                        <div className="flex-shrink-0 bg-orange-100 border border-orange-300 text-orange-800 px-4 py-3 rounded-lg text-center font-bold text-sm shadow-sm">
                            <div className="text-2xl mb-1">📋</div>
                            Formal AAR Required
                        </div>
                    )}
                </div>
            </Section>
        )}

            {result.recommendedChecklists && result.recommendedChecklists.length > 0 && (
                <Section title="Tactical Linking (Recommended Checklists)">
                    <div className="bg-emerald-50 border border-emerald-200 p-5 rounded-lg shadow-sm">
                        <h4 className="font-bold text-emerald-900 text-sm mb-3">Linked Local Incident Management Checklists</h4>
                        <p className="text-emerald-800 text-sm mb-4">
                            The following tactical checklists are recommended for execution to support this strategic plan:
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {safeArray(result.recommendedChecklists).map(checklistLabel).filter(Boolean).map((checklist, i) => (
                                <button 
                                    key={i} 
                                    onClick={() => onLaunchChecklist && onLaunchChecklist(checklist)}
                                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider rounded shadow-sm flex items-center gap-2 transition-colors"
                                >
                                    {ICONS.file} Generate: {checklist}
                                </button>
                            ))}
                        </div>
                    </div>
                </Section>
            )}

            {/* Generic Intelligence Sections */}
            {safeArray((result as any).additionalSections).length > 0 && (
                <div className="space-y-6">
                    {safeArray((result as any).additionalSections).map((section: any, idx: number) => (
                        <Section key={idx} title={section.title}>
                            <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(section.content) }} />
                        </Section>
                    ))}
                </div>
            )}
        </div>
    );

// --- Business Resiliency Display ---
export const BusinessResiliencyDisplay: React.FC<{ result: BusinessResiliencyPlanResult }> = ({ result }) => (
    <div className="space-y-6">
        <AppliedPolicyTemplate result={result} />
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 text-white">
            <h3 className="font-bold text-slate-400 text-sm uppercase">Business Resiliency Configuration</h3>
            <p className="mt-2 text-sm text-slate-300">{(result as any).summary || "Generated resilient strategies mapped to defined critical functions."}</p>
        </div>

        <Section title="Critical Functions (BIA)">
            <div className="grid grid-cols-1 gap-4">
                {result.criticalFunctions?.map((func, i) => (
                    <div key={i} className="bg-white p-5 rounded-lg border shadow-sm flex flex-col md:flex-row gap-6">
                        <div className="flex-1">
                            <h4 className="font-bold text-slate-800 text-lg">{func.functionName}</h4>
                            <div className="mt-3 text-sm text-slate-600 space-y-1">
                                <p><span className="font-semibold text-slate-700">RTO:</span> {func.rto}</p>
                                <p><span className="font-semibold text-slate-700">RPO:</span> {func.rpo}</p>
                            </div>
                        </div>
                        <div className="flex-1 bg-slate-50 p-4 rounded border border-slate-100">
                            <h5 className="font-bold text-xs uppercase text-slate-500 mb-2">Key Dependencies</h5>
                            <ul className="list-disc pl-4 text-sm text-slate-700 space-y-1">
                                {func.dependencies?.map((dep, j) => <li key={j}>{dep}</li>)}
                            </ul>
                        </div>
                    </div>
                ))}
            </div>
        </Section>

        <Section title="Supply Chain Vulnerabilities">
            <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                    <thead className="bg-slate-100 text-slate-600 uppercase text-xs font-bold">
                        <tr>
                            <th className="px-4 py-3 border-b">Vendor / Category</th>
                            <th className="px-4 py-3 border-b">Assessed Risk</th>
                            <th className="px-4 py-3 border-b">Mitigation Strategy</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {result.supplyChainVulnerabilities?.map((vuln, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                                <td className="px-4 py-3 font-semibold text-slate-800">{vuln.vendor}</td>
                                <td className="px-4 py-3 text-red-700">{vuln.risk}</td>
                                <td className="px-4 py-3 text-slate-600 italic">{vuln.mitigation}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Section title="Continuity Strategies">
                <div className="bg-white p-5 rounded-lg border shadow-sm h-full">
                    <ul className="space-y-4">
                        {result.continuityStrategies?.map((strategy, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-800 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                                <span className="text-sm text-slate-700 pt-0.5">{strategy}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

            <Section title="Recovery Phases">
                <div className="space-y-3">
                    {result.recoveryPhases?.map((phase, i) => (
                        <div key={i} className="bg-white p-4 rounded-lg border shadow-sm border-l-4 border-l-teal-500">
                            <h4 className="font-bold text-slate-800 text-sm mb-2">{phase.phase}</h4>
                            <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
                                {phase.actions?.map((act, j) => <li key={j}>{act}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            </Section>
        </div>

        {/* Generic Intelligence Sections */}
        {(result as any).additionalSections && (result as any).additionalSections.length > 0 && (
            <div className="space-y-6">
                {(result as any).additionalSections.map((section: any, idx: number) => (
                    <Section key={idx} title={section.title}>
                        <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(section.content) }} />
                    </Section>
                ))}
            </div>
        )}
    </div>
);

// --- Emergency Preparedness Display ---
export const EmergencyPreparednessDisplay: React.FC<{ result: EmergencyPreparednessPlanResult }> = ({ result }) => (
    <div className="space-y-6">
        <AppliedPolicyTemplate result={result} />
        <div className="bg-amber-50 p-6 rounded-xl border border-amber-200">
            <h3 className="font-bold text-amber-900 text-sm uppercase">Emergency Action Plan (EAP)</h3>
            <h2 className="text-2xl font-black text-amber-800 mt-2">{result.scenario}</h2>
            <div className="mt-4 prose prose-sm text-amber-900 italic" dangerouslySetInnerHTML={{ __html: md.render((result as any).summary || '') }} />
            <div className="mt-4 flex flex-wrap gap-2">
                {result.affectedLocations?.map((loc, i) => (
                    <span key={i} className="px-2 py-1 bg-amber-200 text-amber-900 font-bold rounded text-xs uppercase tracking-wider">{loc}</span>
                ))}
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Section title="Evacuation Routes">
                <div className="bg-white p-5 rounded-lg border shadow-sm h-full">
                    <ul className="space-y-3">
                        {result.evacuationRoutes?.map((route, i) => (
                            <li key={i} className="text-sm text-slate-800 font-medium bg-green-50 px-3 py-2 rounded text-green-900 border border-green-100">
                                🚪 {route}
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

            <Section title="Designated Rally Points">
                <div className="bg-white p-5 rounded-lg border shadow-sm h-full">
                    <ul className="space-y-3">
                        {result.rallyPoints?.map((rp, i) => (
                            <li key={i} className="text-sm text-indigo-900 font-bold bg-indigo-50 px-3 py-2 rounded border border-indigo-100">
                                📍 {rp}
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>
        </div>

        <Section title="Tactical Checklists">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.tacticalChecklists?.map((roleReq, i) => (
                     <div key={i} className="bg-white p-4 rounded-lg border shadow-sm">
                         <h4 className="font-bold text-slate-800 text-sm mb-3 uppercase tracking-wider border-b pb-2">{roleReq.role}</h4>
                         <ul className="space-y-2">
                             {roleReq.tasks?.map((task, j) => (
                                 <li key={j} className="flex gap-2 text-sm text-slate-600">
                                     <input type="checkbox" className="mt-1" defaultChecked={false} />
                                     <span>{task}</span>
                                 </li>
                             ))}
                         </ul>
                     </div>
                ))}
            </div>
        </Section>

        <Section title="Equipment Requirements">
            <div className="bg-white p-5 rounded-lg border shadow-sm flex flex-wrap gap-3">
                {result.equipmentRequirements?.map((eq, i) => (
                    <span key={i} className="px-3 py-1.5 bg-slate-100 border text-slate-700 text-sm rounded-md font-medium inline-flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        {eq}
                    </span>
                ))}
            </div>
            </Section>

        {/* Generic Intelligence Sections */}
        {(result as any).additionalSections && (result as any).additionalSections.length > 0 && (
            <div className="space-y-6">
                {(result as any).additionalSections.map((section: any, idx: number) => (
                    <Section key={idx} title={section.title}>
                        <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(section.content) }} />
                    </Section>
                ))}
            </div>
        )}
    </div>
);

// --- Local Incident Management Action Bar ---
export const LocalIncidentActionBar: React.FC<{
    report: LocalIncidentManagementResult;
    onSave: () => void;
    isSaving: boolean;
    onCustomSave?: () => void;
    customSaveLabel?: string;
    onDownloadPDF: () => void;
    readOnly?: boolean;
}> = ({ report, onSave, isSaving, onCustomSave, customSaveLabel, onDownloadPDF, readOnly = false }) => (
    <div className="bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div className="flex flex-col">
            <div className="flex items-center gap-3">
                <span className="bg-red-500/20 text-red-400 p-2 rounded-lg">
                    {ICONS.shield}
                </span>
                <h1 className="text-xl font-bold text-white tracking-widest uppercase">TACTICAL LOCAL INCIDENT PLAN</h1>
            </div>
            <p className="text-slate-400 text-sm font-mono mt-1">
                INCIDENT: {report.incidentType} | FACILITY: {report.affectedLocation}
            </p>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
            {!readOnly && <button
                onClick={onDownloadPDF}
                disabled={isSaving}
                className={`flex-1 md:flex-none hover:bg-slate-700 bg-slate-600 disabled:bg-slate-800 text-white font-bold py-2 px-6 rounded-lg shadow-lg transition-all flex items-center justify-center uppercase tracking-widest text-xs`}
            >
                Print / Save PDF
            </button>}
            <button 
                onClick={onSave}
                disabled={isSaving}
                className={`flex-1 md:flex-none ${onCustomSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-500'} disabled:bg-slate-600 text-white font-bold py-2 px-6 rounded-lg shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all flex items-center justify-center uppercase tracking-widest text-xs`}
            >
                {isSaving ? 'Processing...' : (customSaveLabel || "Sign & Archive Plan")}
            </button>
        </div>
    </div>
);

// --- Local Incident Management Display ---
export const LocalIncidentManagementDisplay: React.FC<{
    result: LocalIncidentManagementResult;
    onSave?: () => void;
    isSaving?: boolean;
    onCustomSave?: () => void;
    customSaveLabel?: string;
    onDownloadPDF?: () => void;
}> = ({ result: report, onSave, isSaving, onCustomSave, customSaveLabel, onDownloadPDF }) => {
    const reportActions = useReportActions();
    const resolvedSave = onSave || reportActions?.onSave || (() => {});
    const resolvedDownload = onDownloadPDF || reportActions?.onDownloadPdf || (() => {});
    const resolvedSaving = isSaving ?? reportActions?.isSaving ?? false;
    const resolvedCustomSave = onCustomSave || (reportActions?.hasCustomSave ? reportActions.onSave : undefined);
    const resolvedCustomSaveLabel = customSaveLabel || reportActions?.customSaveLabel;

    return (
        <div className="space-y-6">
            <LocalIncidentActionBar 
                report={report} 
                onSave={resolvedSave} 
                isSaving={resolvedSaving}
                onCustomSave={resolvedCustomSave}
                customSaveLabel={resolvedCustomSaveLabel}
                onDownloadPDF={resolvedDownload}
                readOnly={reportActions?.readOnly}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Immediate Actions */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-xl border border-red-200 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-5 bg-red-500 w-32 h-32 rounded-bl-full pointer-events-none" />
                        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b border-slate-100 pb-3 uppercase tracking-widest">
                            <span className="text-red-500">⚡</span> Immediate Life-Safety Actions
                        </h2>
                        <ul className="space-y-3">
                            {report.immediateActions?.map((action: string, i: number) => (
                                <li key={i} className="flex gap-3 text-slate-700">
                                    <span className="text-red-500 font-bold shrink-0">{i + 1}.</span>
                                    <span>{action}</span>
                                </li>
                            ))}
                            {(!report.immediateActions || report.immediateActions.length === 0) && <p className="text-slate-500 text-sm">No immediate actions defined.</p>}
                        </ul>
                    </div>

                    {/* Role Checklists */}
                    <div className="bg-white p-6 rounded-xl border border-blue-200 shadow-sm">
                        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2 border-b border-slate-100 pb-3 uppercase tracking-widest">
                            {ICONS.users} Tactical Role Checklists
                        </h2>
                        <div className="space-y-4">
                            {report.roleChecklists?.map((role: any, idx: number) => (
                                <div key={idx} className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                                    <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                                        {role.role}
                                    </h3>
                                    <ul className="space-y-2">
                                        {role.tasks?.map((task: string, tIdx: number) => (
                                            <li key={tIdx} className="flex gap-2 text-sm text-slate-700 items-start">
                                                <input type="checkbox" className="mt-1 rounded text-blue-600 focus:ring-blue-500" />
                                                <span>{task}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            {(!report.roleChecklists || report.roleChecklists.length === 0) && (
                                <p className="text-slate-500 text-sm">No specific role checklists drafted.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar: Comms & Resources */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <h2 className="text-sm font-bold text-slate-500 mb-4 uppercase tracking-widest border-b border-slate-100 pb-2">
                            Communication Protocol
                        </h2>
                        <div className="prose prose-sm text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(report.communicationProtocol || 'No protocol defined.') }} />
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <h2 className="text-sm font-bold text-slate-500 mb-4 uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center justify-between">
                            Critical Resources
                            {ICONS.shield}
                        </h2>
                        <ul className="space-y-2">
                            {report.criticalResources?.map((res: string, idx: number) => (
                                <li key={idx} className="flex gap-2 text-sm text-slate-700 items-center bg-slate-50 p-2 rounded">
                                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                                    {res}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Generic Intelligence Sections */}
            {(report as any).additionalSections && (report as any).additionalSections.length > 0 && (
                <div className="space-y-6">
                    {(report as any).additionalSections.map((section: any, idx: number) => (
                        <Section key={idx} title={section.title}>
                            <div className="prose prose-sm text-slate-700 max-w-none" dangerouslySetInnerHTML={{ __html: md.render(section.content) }} />
                        </Section>
                    ))}
                </div>
            )}
        </div>
    );
};
